<template>
  <UserRLSWrapper>
    <div class="wrapper">
      <div class="main-content">
        <b-overlay
          :show="authenticating"
          variant="white"
          opacity="1"
          class="h-100vh"
          no-fade
        >
          <template #overlay>
            <b-icon
              icon="three-dots"
              animation="cylon"
              variant="primary"
              scale="2"
              shift-v="8"
              shift-h="8"
              class="mx-auto position-absolute left-0 right-0"
            ></b-icon>
          </template>

          <template v-if="!$route.meta.hideNav">
            <AppNavbar />
          </template>

          <!-- Pushed over while the navigation drawer is docked (LIQ-2643). The offset goes on each
               element rather than a wrapper around them: .router-view must stay a direct child of
               this full-height container, because the full-screen reports (Reconciliation, BDX
               Validation) turn routerView.parentElement into a flex column to fit the screen. -->
          <MaintenanceBanner class="nav-drawer-offset" :style="contentOffsetStyle" />

          <AppContent
            :content-height="contentHeight"
            :offset-style="contentOffsetStyle"
            ref="appContent"
          />

          <template v-if="!$route.meta.hideFooter">
            <AppFooter class="nav-drawer-offset" :style="contentOffsetStyle" />
          </template>
        </b-overlay>
      </div>

      <TermlyWrapper />
    </div>
  </UserRLSWrapper>
</template>

<script>
import { computed, provide, reactive } from '@vue/composition-api'

import { userMixin } from '@mixins'
import { useNavDrawer } from '@composables/useNavDrawer'
import { isHamburgerMenuEnabled } from '@/config/navMenu'
import {
  AuthenticationService,
  CompaniesService,
  DownloadsService,
  ErrorService,
  GRecaptchaService,
  HelpService,
  MembersService,
  ProfileService,
  ReportsService,
  RolesService,
  TokenService
} from '@services'

import AppNavbar from '@components/AppNavbar'
import AppContent from '@components/AppContent'
import AppFooter from '@components/AppFooter'
import TermlyWrapper from '@components/Termly/TermlyWrapper'

import { UserRLSWrapper } from '@components/user'
import { MaintenanceBanner } from '@components/misc'

export default {
  mixins: [userMixin],
  props: {
    authSvc: {
      default() {
        return AuthenticationService
      }
    },
    companySvc: {
      default() {
        return CompaniesService
      }
    },
    downloadsSvc: {
      default() {
        return DownloadsService
      }
    },
    grecaptchaSvc: {
      default() {
        return GRecaptchaService
      }
    },
    helpSvc: {
      default() {
        return HelpService
      }
    },
    memberSvc: {
      default() {
        return MembersService
      }
    },
    profileSvc: {
      default() {
        return ProfileService
      }
    },
    reportSvc: {
      default() {
        return ReportsService
      }
    },
    roleSvc: {
      default() {
        return RolesService
      }
    },
    tokenSvc: {
      default() {
        return TokenService
      }
    },

    ReportsService: () => ReportsService
  },
  components: {
    AppNavbar,
    AppContent,
    AppFooter,
    TermlyWrapper,

    UserRLSWrapper,

    MaintenanceBanner
  },
  setup(props) {
    const authSvc = reactive(props.authSvc)
    const companySvc = reactive(props.companySvc)
    const downloadsSvc = reactive(props.downloadsSvc)
    const grecaptchaSvc = reactive(props.grecaptchaSvc)
    const helpSvc = reactive(props.helpSvc)
    const memberSvc = reactive(props.memberSvc)
    const profileSvc = reactive(props.profileSvc)
    const reportSvc = reactive(props.reportSvc)
    const roleSvc = reactive(props.roleSvc)
    const tokenSvc = reactive(props.tokenSvc)

    provide('authSvc', authSvc)
    provide('companySvc', companySvc)
    provide('downloadsSvc', downloadsSvc)
    provide('grecaptchaSvc', grecaptchaSvc)
    provide('helpSvc', helpSvc)
    provide('memberSvc', memberSvc)
    provide('profileSvc', profileSvc)
    provide('reportSvc', reportSvc)
    provide('roleSvc', roleSvc)
    provide('tokenSvc', tokenSvc)

    provide('ErrorService', ErrorService)
    provide('ReportsService', reactive(ReportsService))

    const { contentOffset } = useNavDrawer()
    // margin, not padding: the banner has its own padding (p-3) that padding-left would override.
    // Only set while docked, so the elements keep their own styles otherwise.
    const contentOffsetStyle = computed(() =>
      '0px' === contentOffset.value ? {} : { marginLeft: contentOffset.value }
    )

    return { contentOffsetStyle }
  },
  data() {
    return {
      authenticating: AuthenticationService.authenticating,
      contentHeight: `100vh`
    }
  },
  watch: {
    $route: function () {
      this.calcContentHeight()
    }
  },
  methods: {
    handlePossibleRedirect() {
      if (
        !this.isUserRole('Guest') &&
        this.$route.query.hasOwnProperty('manage-bdx')
      ) {
        this.$router.push({ name: 'ManageBDX' })
      }
      if (
        !this.currentUser.is_superuser &&
        this.currentUser.company &&
        this.currentUser.company.role.role !== 'TPA' &&
        window.location.pathname === '/cash-call'
      ) {
        this.$router.push({ name: 'Dashboard' })  // Redirect to Dashboard if conditions are met
      }
    },
    calcContentHeight() {
      this.$nextTick(() => {
        const navbarHeight = this.$route.meta.hideNav
          ? 0
          : document.getElementById('main-navbar').clientHeight
        const footerHeight = this.$route.meta.hideFooter
          ? 0
          : document.getElementById('main-footer').clientHeight

        this.contentHeight = `calc(100vh - ${navbarHeight + footerHeight}px)`
      })
    }
  },
  mounted() {
    // The LimeIQ Design System's beige page background ships with the new menu (LIQ-2643).
    if (isHamburgerMenuEnabled()) document.body.classList.add('liq-brand')

    this.handlePossibleRedirect()
    this.calcContentHeight()
  }
}
</script>

<style lang="scss">
.nav-drawer-offset {
  transition: margin-left 200ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

body {
  overflow-x: hidden;

  &.dashboard-page,
  &.reportview-page {
    overflow-y: hidden;
  }

  &.liq-brand {
    background-color: var(--liq-beige-bg);
  }
}
</style>
